import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { ToastController } from '@ionic/angular';
import { finalize } from 'rxjs';

import { GroupManagementService } from '@services/group-management.service';
import { PanelHostService } from '@services/panel-host.service';
import { rosterParticipantFromPanelContext } from '@services/panel-store';
import { TripService } from '@services/trip.service';
import { RosterParticipant } from '../../../models';
import { ContentContainerComponent } from '../../ui/content-container/content-container.component';
import { PanelBodyComponent } from '../../ui/panel-body/panel-body.component';

@Component({
  selector: 'app-password-reset',
  imports: [TranslocoDirective, ContentContainerComponent, PanelBodyComponent],
  templateUrl: './password-reset.component.html',
  styleUrls: ['./password-reset.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PasswordResetComponent {
  private readonly panelHost = inject(PanelHostService);
  private readonly tripService = inject(TripService);
  private readonly groupManagementService = inject(GroupManagementService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);

  readonly submitting = signal(false);

  readonly participant = computed((): RosterParticipant | null =>
    rosterParticipantFromPanelContext(this.panelHost.rosterPanelContext()),
  );

  constructor() {
    effect(() => {
      if (this.panelHost.activePanel() !== 'password-reset') {
        this.submitting.set(false);
      }
    });
  }

  cancel(): void {
    this.panelHost.dismiss();
  }

  confirm(): void {
    const participant = this.participant();
    const trip = this.tripService.currentTrip.value();
    if (!participant || !trip || this.submitting()) {
      return;
    }

    this.submitting.set(true);
    this.groupManagementService
      .resetPassword(trip.id, participant.id)
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(
              marker('group-management.panels.password-reset.success'),
              { name: participant.firstname },
            ),
            duration: 4000,
            color: 'success',
          });
          await toast.present();
          this.panelHost.dismiss();
        },
        error: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(
              marker('group-management.panels.password-reset.error'),
              { name: participant.firstname },
            ),
            duration: 4000,
            color: 'danger',
          });
          await toast.present();
        },
      });
  }
}
